import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { Table, Input, message, Tag, Select, Form, Button, Space, Tooltip, Modal, DatePicker, Card } from 'antd';
import { EyeOutlined, PlayCircleOutlined, FilterOutlined } from '@ant-design/icons';
import { Helmet } from "react-helmet";
import startCase from "lodash/startCase";
import { Typography } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import PreviewMedia from '../components/Library/PreviewMedia';
import PreviewPlaylist from '../components/Playlists/PreviewPlaylist';
import { getMediaIcon } from '../utils/Misc';
import Link from "antd/es/typography/Link";
import RequirePermission from "../components/Common/RequirePermission";

const { Title } = Typography;
const Logs = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [filterText, setFilterText] = useState("");
    const [viewPermission, setViewPermission] = useState(false);
    const [user, setUser] = useState(null);
    const [logs, setLogs] = useState([]);
    const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
    const [selectedModule, setSelectedModule] = useState("");
    const [selectedOperation, setSelectedOperation] = useState("");
    const [selectedDateRange, setSelectedDateRange] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [title, setTitle] = useState("");
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [isPlaylistPreviewModalOpen, setIsPlaylistPreviewModalOpen] = useState(false);
    const [previewMedia, setPreviewMedia] = useState(null);
    const [previewPlaylist, setPreviewPlaylist] = useState(null);

    const operationOptions = [
        {
            value: "insert",
            label: "Insert",
        },
        {
            value: "update",
            label: "Update",
        },
        {
            value: "delete",
            label: "Delete",
        },
        {
            value: "reject",
            label: "Reject",
        },
        {
            value: "approve",
            label: "Approve",
        }
    ];

    const moduleNameOptions = [
        {
            value: "users",
            label: "Users",
        },
        {
            value: "screens",
            label: "Screens",
        },
        {
            value: "playlists",
            label: "Playlists",
        },
        {
            value: "media",
            label: "Media",
        },
        {
            value: "roles",
            label: "Roles",
        },
        {
            value: "schedules",
            label: "Schedules",
        },
        {
            value: "devices",
            label: "Devices",
        }
    ];

    const setQueryParams = (params) => {
        const searchParams = new URLSearchParams(params);
        navigate(`?${searchParams.toString()}`, { replace: true });
    };
    const handleTableChange = (pagination, filters, sorter) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    const handleSearch = (e) => {
        const value = e.target.value;
        const newQueryParams = {
            filter: value,
            limit: pageSize,
            module: selectedModule,
            operation: selectedOperation,
            page: 1,
        };
        setFilterText(value);
        setQueryParams(newQueryParams);
        setCurrentPage(1);
    };

    const handleModuleChange = (value) => {
        setSelectedModule(value);
        const newQueryParams = {
            filter: filterText,
            module: value,
            operation: selectedOperation,
            limit: pageSize,
            page: 1,
        };
        setQueryParams(newQueryParams);
        setCurrentPage(1);
    };

    const handleOperationChange = (value) => {
        setSelectedOperation(value);
        const newQueryParams = {
            filter: filterText,
            operation: value,
            module: selectedModule,
            limit: pageSize,
            page: 1,
        };
        setQueryParams(newQueryParams);
        setCurrentPage(1);
    };

    const handleDateRangeChange = (dates) => {
        setSelectedDateRange(dates || []);
        const newQueryParams = {
            filter: filterText,
            module: selectedModule,
            operation: selectedOperation,
            limit: pageSize,
            page: 1,
        };
        setQueryParams(newQueryParams);
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setFilterText("");
        setSelectedModule("");
        setSelectedOperation("");
        setSelectedDateRange([]);
        setCurrentPage(1);
        setQueryParams({
            filter: "",
            module: "",
            operation: "",
            limit: pageSize,
            page: 1,
        });
    };

    const handleView = async (record) => {
        try {
            setLoading(true);
            const response = await axios.get(
                process.env.REACT_APP_BASE_URL + `/api/logs/${record.id}`
            );
            setLogs(response.data.data.results);
            setTitle(response.data.data.results[0].title);
            setIsLogsModalOpen(true);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            message.error("Error fetching logs data");
            console.log("Error fetching logs:", error);
        }
    };

    const handlePreviewMedia = (mediaData) => {
        setIsPreviewModalOpen(true);
        setPreviewMedia(mediaData);
        console.log(mediaData);
    };

    const handlePreviewPlaylist = (record) => {
        setIsPlaylistPreviewModalOpen(true);
        setPreviewPlaylist({ ...record, media: JSON.parse(record.media_details) });
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const params = {
                filter: filterText,
                limit: pageSize,
                page: currentPage,
                module: selectedModule,
                operation: selectedOperation,
            };
            if (selectedDateRange.length === 2) {
                params.start_date = selectedDateRange[0].format("YYYY-MM-DD");
                params.end_date = selectedDateRange[1].format("YYYY-MM-DD");
            }
            if (selectedDateRange.length === 0) {
                params.start_date = "";
                params.end_date = "";
            }
            const response = await axios.get(
                process.env.REACT_APP_BASE_URL + `/api/logs`,
                { params }
            );
            let records = response.data.data.data;
            setTotalRecords(response.data.data.total);
            setDetail(records);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            console.error("Error fetching logs data", error);
        }
    };

    useEffect(() => {
        const fetchTimer = setTimeout(() => {
            fetchData();
        }, 500);

        return () => clearTimeout(fetchTimer);
    }, [filterText, pageSize, currentPage, selectedModule, selectedOperation, selectedDateRange]);

    useEffect(() => {
        const storedUser = JSON.parse(localStorage.getItem("userData"));
        setUser(storedUser);
        const role_id = storedUser.role_id;
        setViewPermission(role_id === 2);
    }, []);

    const columns = [
        {
            title: "Module",
            dataIndex: "table_name",
            key: "module",
            render: (text) => (
                <span>{startCase(text)}</span>
            )
        },
        {
            title: "Operation Type",
            dataIndex: "action",
            key: "operation_type",
            render: (text) => (
                <Tag color={text === "insert" ? "green" : text === "update" ? "blue" : "red"}>
                    {startCase(text)}
                </Tag>
            )
        },
        {
            title: "Created By",
            dataIndex: "created_by",
            key: "created_by",
            render: (text) => (
                <span>{startCase(text)}</span>
            )
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            render: (text) => (
                <span className="text-small">{dayjs(text).format("YYYY-MM-DD HH:mm")}</span>
            )
        },
        {
            title: "Action",
            render: (_, record) => (
                <Space>
                    {user && (
                        <>
                            {viewPermission && (
                                <Tooltip title="View">
                                    <Button

                                        size="small"
                                        icon={<EyeOutlined />}
                                        onClick={() => {
                                            handleView(record);
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </>
                    )}
                </Space>
            ),
        }
    ];

    const renderDataComparison = (oldData, newData) => {
        const oldObj = typeof oldData === "string" ? JSON.parse(oldData || "{}") : oldData || {};
        const newObj = typeof newData === "string" ? JSON.parse(newData || "{}") : newData || {};

        const allKeys = Array.from(new Set([...Object.keys(oldObj), ...Object.keys(newObj)]));

        return (
            <div style={{ padding: "8px" }}>
                <div
                    style={{
                        display: "flex",
                        fontWeight: 600,
                        borderBottom: "2px solid #e0e0e0",
                        paddingBottom: 6,
                        marginBottom: 8,
                        color: "#333",
                    }}
                >
                    <div style={{ flex: 1 }}>Column</div>
                    <div style={{ flex: 1, textAlign: "center" }}>Old Data</div>
                    <div style={{ flex: 1, textAlign: "center" }}>New Data</div>
                </div>

                {allKeys.map((key) => {
                    const oldVal = oldObj[key];
                    const newVal = newObj[key];
                    let changed = oldVal !== newVal;
                    if (key === "id") changed = true;
                    return (
                        (changed && <div
                            key={key}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                borderBottom: "1px solid #f0f0f0",
                                padding: "6px 0",
                                background: "transparent",
                                borderRadius: 6,
                                marginBottom: 2,
                            }}
                        >
                            <div style={{ flex: 1, color: "#555" }}>{key}</div>
                            <div style={{ flex: 1, textAlign: "center", color: "#888" }}>
                                {oldVal === null ? "null" : String(oldVal)}
                            </div>
                            <div
                                style={{
                                    flex: 1,
                                    textAlign: "center",
                                    color: "black",
                                    fontWeight: 400,
                                }}
                            >
                                {newVal === null ? "null" : String(newVal)}
                            </div>
                        </div>)
                    );
                })}
            </div>
        );
    };

    const columnsLogs = [
        {
            title: `${title ?? "No Title"}`,
            key: "dataComparison",
            render: (_, record) => (
                <>
                    <div style={{ marginBottom: "1rem", display: "flex", justifyContent: "space-between" }}>
                        <span>{`Module: ${record.table_name}`}</span>
                        <span>{`Operation: ${record.action}`}</span>
                        <span>{`Modified By: ${record.created_by}`}</span>
                        {(record.table_name === "media") && (<Link href="#" onClick={() => handlePreviewMedia(record.media)}>
                            <Tag>{getMediaIcon(record.media.media_type)} Preview</Tag>
                        </Link>)}
                        {(record.table_name === "playlists") && (<Link href="#" onClick={() => handlePreviewPlaylist(record.playlist)}>
                            <Tag color='green'><PlayCircleOutlined style={{ marginRight: "5px" }}/> Preview </Tag>
                        </Link>)}
                    </div>
                    <Card size="small" style={{ background: "#fafafa", borderRadius: 8 }}>
                        {renderDataComparison(record.old_data, record.new_data)}
                    </Card>
                </>
            ),
        },
    ];

    return (
        <RequirePermission permission="signage.logs_view">
        <div style={{ padding: "1rem" }}>
            <Helmet>
                <title>{`${process.env.REACT_APP_NAME} | Logs`}</title>
            </Helmet>
            <Title level={3} style={{ marginBottom: "1rem" }}>
                Logs
            </Title>
            <div className="d-sm-block d-md-flex justify-content-between mb-3">
                <Form layout="inline">
                    <Form.Item>
                        <Input.Search
                            placeholder="Search logs"
                            value={filterText}
                            className="me-1"
                            onChange={handleSearch}
                            style={{ width: 200 }}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Button
                            icon={<FilterOutlined />}
                            type={showFilters ? "primary" : "default"}
                            onClick={() => setShowFilters((prev) => !prev)}
                        >
                            Filters
                            {(selectedModule || selectedOperation || selectedDateRange.length === 2) && (
                                <Tag color="blue" style={{ marginLeft: 6, marginRight: 0 }}>
                                    {[selectedModule, selectedOperation, selectedDateRange.length === 2 ? "date" : ""].filter(Boolean).length}
                                </Tag>
                            )}
                        </Button>
                    </Form.Item>
                </Form>
            </div>

            {showFilters && (
                <Form layout="inline" className="mb-3">
                    <Form.Item label="Module">
                        <Select
                            value={selectedModule}
                            allowClear
                            onChange={handleModuleChange}
                            style={{ width: 200 }}
                            showSearch
                            filterOption={(input, option) => {
                                return (option?.children ?? "")
                                    .toLowerCase()
                                    .includes(input.toLowerCase());
                            }}
                        >
                            {moduleNameOptions.map((module) => (
                                <Select.Option key={module.value} value={module.value}>
                                    {module.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="Operation">
                        <Select
                            value={selectedOperation}
                            allowClear
                            onChange={handleOperationChange}
                            style={{ width: 200 }}
                            showSearch
                            filterOption={(input, option) => {
                                return (option?.children ?? "")
                                    .toLowerCase()
                                    .includes(input.toLowerCase());
                            }}
                        >
                            {operationOptions.map((opr) => (
                                <Select.Option key={opr.value} value={opr.value}>
                                    {startCase(opr.label)}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="Date">
                        <DatePicker.RangePicker
                            value={selectedDateRange}
                            allowClear
                            onChange={handleDateRangeChange}
                            onCancel={() => {
                                setSelectedDateRange([]);
                            }}
                            style={{ width: 250 }}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Button onClick={handleClearFilters}>
                            Clear Filters
                        </Button>
                    </Form.Item>
                </Form>
            )}
            <Table
                loading={loading}
                columns={columns}
                dataSource={detail}
                scroll={{ x: "max-content" }}
                rowKey={(record) => record.id}
                onChange={handleTableChange}
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalRecords,
                    showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} locations`,
                    showSizeChanger: true, // Enables the page size selection dropdown
                    pageSizeOptions: ["5", "10", "20", "50", "100"], // Options for the page size selection
                }}
            />
            {isLogsModalOpen && (
                <Modal
                    title="Log Details"
                    open={isLogsModalOpen}
                    onCancel={() => setIsLogsModalOpen(false)}
                    footer={null}
                    width={800}
                >
                    <Table
                        loading={loading}
                        columns={columnsLogs}
                        dataSource={logs}
                        scroll={{ x: "max-content" }}
                        rowKey={(record) => record.id}
                        pagination={false}
                    />
                </Modal>
            )}
            {isPreviewModalOpen && (
                <PreviewMedia
                    isModalOpen={isPreviewModalOpen}
                    closeModal={() => setIsPreviewModalOpen(false)}
                    media={previewMedia}
                />
            )}
            {isPlaylistPreviewModalOpen && (
                <PreviewPlaylist
                    isModalOpen={isPlaylistPreviewModalOpen}
                    closeModal={() => setIsPlaylistPreviewModalOpen(false)}
                    playlist={previewPlaylist}
                />
            )}
        </div>
        </RequirePermission>
    )
}

export default Logs;